import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const STATUS_OPTIONS = ['출석', '결석', '지각', '조퇴', '공결'];
const MAX_PERIODS = 8;
const ABSENCE_LIKE_STATUSES = ['결석', '지각', '조퇴'];

const pad2 = (n) => String(n).padStart(2, '0');
// toISOString()은 UTC 날짜라 한국시간 자정~오전 9시에 전날로 나오므로, 기기 로컬 날짜로 만든다
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

function defaultRow(active = {}) {
  const periodValues = {};
  for (let p = 1; p <= MAX_PERIODS; p += 1) {
    periodValues[p] = active[p] ? 1 : '';
  }
  return { attstatus: '출석', reason: '', periodValues };
}

function allPeriodsInactive() {
  const next = {};
  for (let p = 1; p <= MAX_PERIODS; p += 1) next[p] = false;
  return next;
}

export default function MobileAttendance() {
  const { user } = useAuth();
  const isAdminStaff = user?.role === 'admin' || user?.role === 'staff';
  const [satdate, setSatdate] = useState(today());
  const [grade, setGrade] = useState(1);
  const [classtype, setClasstype] = useState('M');
  const [classOptions, setClassOptions] = useState([]);
  const [classNo, setClassNo] = useState('');
  const [plans, setPlans] = useState([]);
  const [crsuid, setCrsuid] = useState('');
  const [daytime, setDaytime] = useState(MAX_PERIODS);
  const [students, setStudents] = useState([]);
  // 표에 보여줄 교시(헤더 콤보박스로 고름)와, 교시별 "지금 입력 중" 체크 상태
  const [viewPeriod, setViewPeriod] = useState(1);
  const [periodsActive, setPeriodsActive] = useState(allPeriodsInactive());
  const [rows, setRows] = useState({});
  const [savedPeriods, setSavedPeriods] = useState(new Set());
  const [savedPeriodAmtcode, setSavedPeriodAmtcode] = useState({});
  const [savedPeriodTptype, setSavedPeriodTptype] = useState({});
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const year = useMemo(() => Number(satdate.slice(0, 4)), [satdate]);

  useEffect(() => {
    api.get('/lesson-plans', { params: { year } }).then((res) => {
      setPlans(res.data);
      setCrsuid((prev) => (prev && res.data.some((p) => p.crsuid === prev) ? prev : res.data[0]?.crsuid ?? ''));
    });
  }, [year]);

  const selectedPlan = useMemo(() => plans.find((p) => p.crsuid === crsuid), [plans, crsuid]);

  const chapterGroups = useMemo(() => {
    if (!selectedPlan) return [];
    const order = [];
    const byKey = new Map();
    selectedPlan.sections.forEach((s) => {
      const key = s.crsmatno || s.chapter || selectedPlan.chapter;
      if (!byKey.has(key)) {
        byKey.set(key, {
          chapter: s.chapter || selectedPlan.chapter,
          crsmatno: s.crsmatno || key,
          amtcode: s.amtcode || null,
          thrytime: 0,
          practime: 0,
        });
        order.push(key);
      }
      const g = byKey.get(key);
      g.thrytime += s.thrytime || 0;
      g.practime += s.practime || 0;
    });
    return order.map((key) => byKey.get(key));
  }, [selectedPlan]);

  const [selectedChapterKey, setSelectedChapterKey] = useState('');
  const [selectedTptype, setSelectedTptype] = useState('T');

  useEffect(() => {
    const first = chapterGroups[0];
    setSelectedChapterKey(first?.crsmatno ?? '');
    setSelectedTptype(first && !first.thrytime && first.practime ? 'P' : 'T');
  }, [chapterGroups]);

  useEffect(() => {
    if (!selectedPlan) return;
    const fallback = (selectedPlan.thrytime || 0) + (selectedPlan.practime || 0);
    const next = selectedPlan.daytime || fallback || MAX_PERIODS;
    setDaytime(Math.min(MAX_PERIODS, Math.max(1, next)));
    setViewPeriod(1);
  }, [selectedPlan]);

  const periodCount = useMemo(
    () => Math.min(MAX_PERIODS, Math.max(1, Number(daytime) || 1)),
    [daytime]
  );
  const PERIODS = useMemo(() => Array.from({ length: periodCount }, (_, i) => i + 1), [periodCount]);

  useEffect(() => {
    if (viewPeriod > periodCount) setViewPeriod(periodCount);
  }, [periodCount, viewPeriod]);

  useEffect(() => {
    setClassNo('');
    setStudents([]);
    setRows({});
    setAlreadySubmitted(false);
    api.get('/classes', { params: { grade, classtype } }).then((res) => setClassOptions(res.data));
  }, [grade, classtype]);

  useEffect(() => {
    if (classNo === '') {
      setStudents([]);
      return;
    }
    let cancelled = false;
    api.get('/students', { params: { grade, class_no: classNo, classtype } }).then((res) => {
      if (!cancelled) setStudents(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [grade, classNo, classtype]);

  const loadSession = useCallback(async () => {
    if (!crsuid || classNo === '') {
      setRows({});
      setAlreadySubmitted(false);
      setPeriodsActive(allPeriodsInactive());
      setSavedPeriods(new Set());
      setSavedPeriodAmtcode({});
      setSavedPeriodTptype({});
      return;
    }
    const res = await api.get('/teacher/attendance', {
      params: { satdate, crsuid, classtype, class_no: classNo },
    });
    if (res.data.length > 0) {
      const next = {};
      const loadedPeriods = new Set();
      const amtcodeByPeriod = {};
      const tptypeByPeriod = {};
      res.data.forEach((r) => {
        loadedPeriods.add(r.period);
        if (!next[r.studid]) next[r.studid] = defaultRow();
        next[r.studid].periodValues[r.period] = r.crdtime;
        if (r.amtcode && !amtcodeByPeriod[r.period]) amtcodeByPeriod[r.period] = r.amtcode;
        if (r.tptype && !tptypeByPeriod[r.period]) tptypeByPeriod[r.period] = r.tptype;
        if (next[r.studid].attstatus === '출석' || r.attstatus !== '출석') {
          next[r.studid].attstatus = r.attstatus;
          next[r.studid].reason = r.reason || '';
        }
      });
      setRows(next);
      // 이미 저장된 교시는 값만 보여주고 체크 해제 상태로 시작한다 (다시 고치려면 직접 체크)
      setPeriodsActive(allPeriodsInactive());
      setSavedPeriods(loadedPeriods);
      setSavedPeriodAmtcode(amtcodeByPeriod);
      setSavedPeriodTptype(tptypeByPeriod);
      setAlreadySubmitted(true);
    } else {
      setAlreadySubmitted(false);
      setRows({});
      setPeriodsActive(allPeriodsInactive());
      setSavedPeriods(new Set());
      setSavedPeriodAmtcode({});
      setSavedPeriodTptype({});
    }
  }, [satdate, crsuid, classtype, classNo]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const rowFor = (studid) => rows[studid] || defaultRow(periodsActive);

  const updateRow = (studid, patch) => {
    setRows((prev) => ({ ...prev, [studid]: { ...rowFor(studid), ...patch } }));
  };

  const updatePeriodValue = (studid, period, value) => {
    const current = rowFor(studid);
    setRows((prev) => ({
      ...prev,
      [studid]: { ...current, periodValues: { ...current.periodValues, [period]: value } },
    }));
  };

  // 교시 체크: 저장 전 교시는 체크 시 기본값(출석=1, 결석/지각/조퇴=0)으로 채우고, 해제하면 비운다.
  // 이미 저장된 교시는 저장된 값을 그대로 둔다.
  const setPeriodActive = (period, nextValue) => {
    setPeriodsActive((prev) => ({ ...prev, [period]: nextValue }));
    if (!savedPeriods.has(period)) {
      setRows((prevRows) => {
        const updated = {};
        Object.entries(prevRows).forEach(([studid, row]) => {
          const fillValue = ABSENCE_LIKE_STATUSES.includes(row.attstatus) ? 0 : 1;
          updated[studid] = {
            ...row,
            periodValues: { ...row.periodValues, [period]: nextValue ? fillValue : '' },
          };
        });
        return updated;
      });
    }
  };

  // 반을 고른 뒤/교시를 바꾼 뒤, 아직 저장 전인 교시를 보고 있으면 바로 입력할 수 있게 자동으로 체크해준다
  useEffect(() => {
    if (classNo === '' || !crsuid) return;
    if (savedPeriods.has(viewPeriod) || periodsActive[viewPeriod]) return;
    setPeriodActive(viewPeriod, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedPeriods, viewPeriod]);

  const creditFor = (studid) => {
    const r = rowFor(studid);
    return Array.from({ length: MAX_PERIODS }, (_, i) => i + 1)
      .filter((p) => periodsActive[p] || savedPeriods.has(p))
      .reduce((sum, p) => sum + (Number(r.periodValues[p]) || 0), 0);
  };

  const handleReset = async (periodsToClear) => {
    setSaving(true);
    setMessage('');
    try {
      const periodsToKeep = Array.from({ length: MAX_PERIODS }, (_, i) => i + 1).filter(
        (p) => savedPeriods.has(p) && !periodsToClear.includes(p)
      );
      await api.post('/teacher/attendance', {
        satdate,
        grade: Number(grade),
        classtype,
        class_no: Number(classNo),
        crsuid,
        entries: students.map((s) => {
          const r = rowFor(s.studid);
          return {
            studid: s.studid,
            attstatus: r.attstatus,
            reason: r.reason || null,
            periods: periodsToKeep.map((p) => ({
              period: p,
              crdtime: Number(r.periodValues[p]) || 0,
              amtcode: savedPeriodAmtcode[p] || null,
              tptype: savedPeriodTptype[p] || null,
            })),
          };
        }),
      });
      setMessage('선택한 교시의 출석자료가 삭제되었습니다.');
      await loadSession();
    } catch (err) {
      setMessage(err.response?.data?.detail || '삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetClick = () => {
    const periodsToClear = PERIODS.filter((p) => periodsActive[p]);
    if (periodsToClear.length === 0) {
      setMessage('초기화할 교시를 먼저 체크하세요.');
      return;
    }
    const periodLabel = periodsToClear.map((p) => `${p}교시`).join(', ');
    if (window.confirm(`${periodLabel} 출석자료를 삭제합니다.\n\n정말 삭제하시겠습니까?`)) {
      handleReset(periodsToClear);
    }
  };

  const selectedChapterAmtcode = () => {
    const key = selectedChapterKey || chapterGroups[0]?.crsmatno;
    return chapterGroups.find((g) => g.crsmatno === key)?.amtcode || null;
  };

  const selectedChapterLabel = () => {
    const key = selectedChapterKey || chapterGroups[0]?.crsmatno;
    return chapterGroups.find((g) => g.crsmatno === key)?.chapter || selectedPlan?.chapter || '';
  };

  const tptypeLabel = (t) => (t === 'P' ? '실기' : t === 'T' ? '학과' : '');

  // 지금 체크되어 편집 중인 교시는 위에서 고른 학과/실기를, 체크 안 된 저장된 교시는 저장 당시 값을 보여준다
  const periodTptypeLabel = (p) => {
    if (periodsActive[p]) return tptypeLabel(selectedTptype);
    if (savedPeriods.has(p)) return tptypeLabel(savedPeriodTptype[p]);
    return '';
  };

  const periodInputValue = (p, raw) => {
    const label = periodTptypeLabel(p);
    if (!label) return raw === '' ? '' : String(raw);
    return raw === '' ? label : `${label} ${raw}`;
  };

  const handlePeriodInputChange = (studid, p, rawText) => {
    const digits = rawText.replace(/\D/g, '');
    updatePeriodValue(studid, p, digits === '' ? '' : digits);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const periodsToSave = Array.from({ length: MAX_PERIODS }, (_, i) => i + 1).filter(
        (p) => periodsActive[p] || savedPeriods.has(p)
      );
      await api.post('/teacher/attendance', {
        satdate,
        grade: Number(grade),
        classtype,
        class_no: Number(classNo),
        crsuid,
        entries: students.map((s) => {
          const r = rowFor(s.studid);
          return {
            studid: s.studid,
            attstatus: r.attstatus,
            reason: r.reason || null,
            periods: periodsToSave.map((p) => ({
              period: p,
              crdtime: Number(r.periodValues[p]) || 0,
              amtcode: periodsActive[p] ? selectedChapterAmtcode() : savedPeriodAmtcode[p] || null,
              tptype: periodsActive[p] ? selectedTptype : savedPeriodTptype[p] || null,
            })),
          };
        }),
      });
      setMessage(alreadySubmitted ? '출결 정보가 수정되었습니다.' : '출결이 저장되었습니다.');
      await loadSession();
    } catch (err) {
      setMessage(err.response?.data?.detail || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (satdate > today()) {
      setMessage('미래 날짜는 출결처리를 할 수 없습니다.');
      return;
    }
    const activePeriods = PERIODS.filter((p) => periodsActive[p]);
    if (activePeriods.length === 0) {
      handleSave();
      return;
    }
    const periodLabel = activePeriods.map((p) => `${p}교시`).join(', ');
    const confirmMessage = `${periodLabel}에 "${selectedChapterLabel()}(${tptypeLabel(selectedTptype)})" 수업으로 출결처리 하겠습니다.\n\n이대로 저장하시겠습니까?`;
    if (window.confirm(confirmMessage)) {
      handleSave();
    }
  };

  const viewPeriodChecked = !!periodsActive[viewPeriod];

  return (
    <div className="page mobile-attendance-page">
      <div className="card">
        <div className="mob-att-row">
          <div className="mob-att-field mob-att-field-date">
            <label>일자</label>
            <input type="date" value={satdate} max={today()} onChange={(e) => setSatdate(e.target.value)} />
          </div>
          <div className="mob-att-field">
            <label>일일 수업시간</label>
            <select value={daytime} onChange={(e) => setDaytime(Number(e.target.value))}>
              {Array.from({ length: MAX_PERIODS }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{n}시간</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mob-att-row">
          <div className="mob-att-field">
            <label>교육계획서</label>
            <select value={crsuid} onChange={(e) => setCrsuid(Number(e.target.value))}>
              <option value="">-- 선택하세요 --</option>
              {plans.map((p) => (
                <option key={p.crsuid} value={p.crsuid}>
                  {p.unitno}차시 {p.subject}{isAdminStaff && p.teacher_name ? ` (${p.teacher_name})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {chapterGroups.length > 0 && (
          <div className="mob-att-row">
            <div className="mob-att-field">
              <label>교육항목</label>
              <select value={selectedChapterKey} onChange={(e) => setSelectedChapterKey(e.target.value)}>
                {chapterGroups.map((g) => (
                  <option key={g.crsmatno} value={g.crsmatno}>{g.chapter}</option>
                ))}
              </select>
            </div>
            <div className="tptype-radio-group mob-att-tptype">
              <label className="tptype-radio">
                <input type="radio" name="mob-tptype" checked={selectedTptype === 'T'} onChange={() => setSelectedTptype('T')} />
                학과
              </label>
              <label className="tptype-radio">
                <input type="radio" name="mob-tptype" checked={selectedTptype === 'P'} onChange={() => setSelectedTptype('P')} />
                실기
              </label>
            </div>
          </div>
        )}

        <div className="mob-att-row">
          <div className="mob-att-field">
            <select value={classtype} onChange={(e) => setClasstype(e.target.value)}>
              <option value="R">정규반</option>
              <option value="M">면장반</option>
            </select>
          </div>
          <div className="mob-att-field mob-att-field-inline">
            <select value={grade} onChange={(e) => setGrade(Number(e.target.value))}>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
            <span>학년</span>
          </div>
          <div className="mob-att-field mob-att-field-inline">
            <select value={classNo} onChange={(e) => setClassNo(e.target.value)}>
              <option value="">-</option>
              {classOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <span>반</span>
          </div>
          <button
            className="btn-primary btn-inline mob-att-save-btn"
            type="button"
            onClick={handleSaveClick}
            disabled={saving || students.length === 0}
          >
            {saving ? '저장 중...' : PERIODS.some((p) => periodsActive[p] && savedPeriods.has(p)) ? '수정 저장' : '출결저장'}
          </button>
        </div>
      </div>

      <div className="card">
        {alreadySubmitted && (
          <div className="form-message">이미 입력된 출결입니다. 저장하면 이 내용으로 덮어씁니다.</div>
        )}

        <table className="data-table mob-att-table">
          <colgroup>
            <col style={{ width: '8%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '24%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '23%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>학번</th>
              <th>이름</th>
              <th>
                <div className="mob-att-period-head">
                  <input
                    type="checkbox"
                    checked={viewPeriodChecked}
                    onChange={() => setPeriodActive(viewPeriod, !viewPeriodChecked)}
                  />
                  <select value={viewPeriod} onChange={(e) => setViewPeriod(Number(e.target.value))}>
                    {PERIODS.map((p) => (
                      <option key={p} value={p}>{p}교시</option>
                    ))}
                  </select>
                </div>
              </th>
              <th>출결</th>
              <th>출결이유</th>
              <th>시수</th>
            </tr>
          </thead>
          <tbody>
            {students.map((s) => {
              const r = rowFor(s.studid);
              return (
                <tr key={s.studid}>
                  <td>{s.studno}</td>
                  <td>{s.name}</td>
                  <td>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="period-value-input"
                      disabled={!viewPeriodChecked}
                      value={periodInputValue(viewPeriod, r.periodValues[viewPeriod])}
                      onChange={(e) => handlePeriodInputChange(s.studid, viewPeriod, e.target.value)}
                    />
                  </td>
                  <td>
                    <select
                      value={r.attstatus}
                      onChange={(e) => {
                        const nextStatus = e.target.value;
                        // 지금 체크된(편집 중인) 교시만 상태에 맞춰 다시 채운다: 결석/지각/조퇴=0, 출석/공결=1
                        const fillValue = ABSENCE_LIKE_STATUSES.includes(nextStatus) ? 0 : 1;
                        const updatedValues = { ...r.periodValues };
                        for (let p = 1; p <= MAX_PERIODS; p += 1) {
                          if (periodsActive[p]) updatedValues[p] = fillValue;
                        }
                        updateRow(s.studid, { attstatus: nextStatus, periodValues: updatedValues });
                      }}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="reason-input"
                      disabled={r.attstatus === '출석'}
                      value={r.reason || ''}
                      onChange={(e) => updateRow(s.studid, { reason: e.target.value })}
                    />
                  </td>
                  <td className="credit-total-cell">{creditFor(s.studid)}</td>
                </tr>
              );
            })}
            {students.length === 0 && (
              <tr>
                <td colSpan={6} className="empty-row">
                  {classNo === '' ? '반을 선택하면 명단이 표시됩니다.' : '해당 학년/반 학생이 없습니다.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {message && <div className="form-message">{message}</div>}

        <div className="attendance-actions">
          <button className="btn-ghost" type="button" onClick={handleResetClick} disabled={saving}>출석자료 초기화</button>
        </div>
      </div>
    </div>
  );
}
